import type { MouseEventHandler } from 'react';
import { useCallback, useMemo, useState } from 'react';
import useAuthContext from '@salesforce/commerce-sdk-react/hooks/useAuthContext';
import { useServerContext } from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks';
import { isServer, useInfiniteQuery } from '@tanstack/react-query';
import { useHistory, useLocation } from 'react-router-dom';
import type { History, Location } from 'history';
import { bffGet } from '../../../../bff-api/bff';
import { getProductSearchHeaders } from '../../../../bff-api/bloomreach-recommendation/useBloomreachSearch';
import { BffQueryKey } from '../../../../bff-api/model/BffQueryKey';
import {
    convertSearchParamsToURLParams,
    convertURLParamsToSearchParams,
    createServiceURLSearchParams,
} from '../../utils/search-url-params-utils';
import { BloomreachProductType } from './use-search-types';
import type { BFFHttpError } from '../../../../bff-api/model/BFFHttpError';
import type {
    CategoryNode,
    SearchParams,
    SearchResult,
    SearchSideEffects,
    UseSearchResult,
} from './use-search-types';

export const useSearch = (
    productType: BloomreachProductType,
    categoryPath?: string,
): UseSearchResult => {
    const authContext = useAuthContext();
    const serverContext = useServerContext();

    const history = useHistory();
    const location = useLocation();

    const { pathname, search } = location;
    const params = useMemo(() => new URLSearchParams(search), [search]);

    const initialSearchParams = useMemo(() => {
        const urlProductType = (params.get('productType') || 'hardgood') as BloomreachProductType;
        const initialSearchParameters =
            productType === urlProductType
                ? convertURLParamsToSearchParams({
                      urlParams: params,
                      productType,
                      categoryId: categoryPath,
                  })
                : {
                      productType,
                      ...(categoryPath ? { categoryId: categoryPath } : {}),
                      ...(params.get('q') ? { q: params.get('q') || '' } : {}),
                  };
        return initialSearchParameters;
        // This just runs once on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [searchParams, setSearchParams] = useState<SearchParams>(initialSearchParams);

    const searchKey = useMemo(() => {
        const _params = createServiceURLSearchParams(searchParams);
        // The queryKey for useInfiniteQuery in React Query should generally not contain the page number.
        // This is because the page number is handled separately and is not part of the queryKey.
        // If not removed, going back from a PDP page, for example, will trigger
        // a new search which will show the previous button what desynchronizes the position in the page.
        // Causing: SLTBE-6597
        _params.delete('page');
        return _params.toString();
    }, [searchParams]);

    const results = useInfiniteQuery<SearchResult, BFFHttpError>({
        queryFn: async ({ pageParam }) => {
            if (!isServer) await window.sfccSessionCreationPromise;
            const authData = await authContext.ready();

            const urlSearchParams = createServiceURLSearchParams(searchParams);
            if (pageParam >= 0) {
                urlSearchParams.set('page', String(pageParam));
            }

            return bffGet<SearchResult>('/bloomreach/product-search', {
                httpParams: Array.from(urlSearchParams).map(([key, value]) => ({
                    [key]: value,
                })),
                accessToken: authData.access_token,
                customerId: authData.customer_id,
                headers: getProductSearchHeaders(serverContext.req),
            });
        },
        getPreviousPageParam: (_: SearchResult, allPages: SearchResult[]): undefined | number => {
            const smallestPage = Math.min(
                ...allPages
                    .map((page) => page.page.page)
                    .filter((pageNumber) => typeof pageNumber === 'number'),
            );
            return smallestPage > 0 ? smallestPage - 1 : undefined;
        },
        getNextPageParam: (
            lastPage: SearchResult,
            allPages: SearchResult[],
        ): undefined | number => {
            const { pageSize, total } = lastPage.page;
            const totalPages = Math.ceil(total / pageSize);
            const biggestPage = Math.max(
                0,
                ...allPages
                    .map((page) => page.page.page)
                    .filter((pageNumber) => typeof pageNumber === 'number'),
            );
            return biggestPage + 1 < totalPages ? biggestPage + 1 : undefined;
        },
        queryKey: [BffQueryKey.SEARCH, searchKey],
        enabled: !!searchParams.q || !!categoryPath,
        keepPreviousData: true,
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
    });

    const handleNextPage: MouseEventHandler<HTMLButtonElement> = useCallback(
        async (e): Promise<void> => {
            e.preventDefault();
            const newResults = await results.fetchNextPage();
            const newLastPage = Math.max(
                ...(newResults?.data?.pages?.map((page) => page.page.page) ?? [0]),
            );
            params.set('page', (newLastPage + 1).toString());
            history.replace(`${pathname}?${params.toString()}`);
        },
        [results, params, history, pathname],
    );

    const handlePreviousPage: MouseEventHandler<HTMLButtonElement> = useCallback(
        async (e): Promise<void> => {
            e.preventDefault();
            await results.fetchPreviousPage();
        },
        [results],
    );

    const { currentPage, total, items, filters, isCustomPrice, categoriesMap } = useMemo(() => {
        const currentFilters = results?.data?.pages[0]?.filters;
        let hasCustomPrice = false;

        const [price] = convertSearchParamsToURLParams(searchParams).getAll('price');
        if (price) {
            // Check if value matches any of the payload's start-end
            hasCustomPrice = !currentFilters?.byRange?.price?.some(
                (range) => `${range.start}-${range.end}` === price,
            );
        }

        const _categoriesMap: Record<string, CategoryNode> = (currentFilters?.categories || [])
            // Flatten the children of the categories
            .flatMap((category): CategoryNode[] =>
                (category.children ?? []).map((child) => ({
                    ...child,
                    type: 'T3',
                })),
            )
            // Add the parent categories without children
            .concat(
                (currentFilters?.categories || []).map((category) => ({
                    ...category,
                    type: 'T2',
                    children: [],
                })),
            )
            .filter((category): category is CategoryNode => !!category?.id)
            .reduce((acc, curr) => ({ ...acc, [curr.id]: curr }), {});

        return {
            items:
                results?.data?.pages
                    .map((page) =>
                        page.items.map((item) => ({
                            ...item,
                            pageNumber: page.page.page,
                        })),
                    )
                    .flat() ?? [],
            total: results?.data?.pages[0]?.page.total ?? 0,
            currentPage: Math.max(...(results?.data?.pages?.map((p) => p.page?.page ?? 0) ?? [0])),
            isCustomPrice: hasCustomPrice,
            filters: currentFilters,
            categoriesMap: _categoriesMap,
        };
    }, [results, searchParams]);

    const {
        onSelected,
        onRemoveFilter,
        onClearAllFilters,
        onChangeRangeFilter,
        onChangeFilter,
        onChangeParam,
        activeFilters,
        isFilterSelected,
        onBulkChangeFilter,
    } = useMemo(
        () =>
            searchSideEffects(
                productType,
                currentPage,
                searchParams,
                setSearchParams,
                location,
                history,
                categoriesMap,
            ),
        [productType, currentPage, searchParams, location, history, categoriesMap],
    );

    const nextPageUrl = useMemo<string | undefined>(() => {
        if (!results?.hasNextPage) {
            return undefined;
        }

        const newParams = convertSearchParamsToURLParams({
            q: searchParams.q,
            productType,
            page: currentPage + 1,
        });
        return `?${newParams.toString()}`;
    }, [results.hasNextPage, searchParams.q, currentPage, productType]);

    const redirectUrl = results?.data?.pages[0]?.redirectUrl;

    return {
        productType,
        onSelected,
        hasPreviousPage: results.hasPreviousPage,
        hasNextPage: results.hasNextPage,
        isLoading: results.isLoading,
        isFetchingNextPage: results.isFetchingNextPage,
        isFetchingPreviousPage: results.isFetchingPreviousPage,
        fetchPreviousPage: results.fetchPreviousPage,
        isFetching: results.isFetching,
        currentPage,
        total,
        items,
        filters,
        nextPageUrl,
        isCustomPrice,
        categoriesMap,
        handleNextPage,
        activeFilters,
        handlePreviousPage,
        error: results.error,
        searchParams,
        onClearAllFilters,
        onRemoveFilter,
        onChangeFilter,
        onChangeRangeFilter,
        isFilterSelected,
        onBulkChangeFilter,
        onChangeParam,
        redirectUrl,
    };
};

function extractRequiredSearchParams(
    searchParams: Pick<SearchParams, 'productType' | 'categoryId' | 'q' | 'sort' | 'page'>,
): SearchParams {
    return {
        productType: searchParams.productType,
        ...(searchParams.categoryId ? { categoryId: searchParams.categoryId } : {}),
        ...(searchParams.q ? { q: searchParams.q } : {}),
        ...(searchParams.sort ? { sort: searchParams.sort } : {}),
    };
}

const searchSideEffects = (
    productType: BloomreachProductType,
    currentPage: number,
    // page is handled separately because it's part of the infinite scroll logic
    searchParams: Omit<SearchParams, 'page'>,
    setSearchParams: (params: SearchParams) => void,
    location: Location,
    history: History,
    categoriesMap: Record<string, CategoryNode>,
): SearchSideEffects => {
    const onRemoveFilter = (type: string, value?: string): void => {
        const params = new URLSearchParams(location.search);
        if (params.get('page')) {
            params.delete('page');
        }
        if (value) {
            // @ts-expect-error - Using polyfilled URLSearchParams.delete with two arguments
            params.delete(type, value);
        } else {
            params.delete(type);
        }
        history.replace(`${location.pathname}?${params.toString()}`);
        setSearchParams(
            convertURLParamsToSearchParams({
                urlParams: params,
                productType,
                categoryId: searchParams.categoryId,
            }),
        );

        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const onClearAllFilters = (): void => {
        const params = convertSearchParamsToURLParams(extractRequiredSearchParams(searchParams));
        if (params.get('page')) {
            params.delete('page');
        }
        history.replace(`${location.pathname}?${params.toString()}`);
        setSearchParams(
            convertURLParamsToSearchParams({
                urlParams: params,
                productType,
                categoryId: searchParams.categoryId,
            }),
        );

        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const onSelected = (): void => {
        history.replace(
            `${location.pathname}?${convertSearchParamsToURLParams({
                ...searchParams,
                productType,
                page: currentPage,
            }).toString()}`,
        );
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const onChangeParam = (type: string, value: string, updateURL = true): void => {
        const params = new URLSearchParams(location.search);
        if (params.get('page')) {
            params.delete('page');
        }
        if (value) {
            params.set(type, value);
        } else {
            params.delete(type);
        }
        if (updateURL) {
            history.replace(`${location.pathname}?${params.toString()}`);
        }
        setSearchParams(
            convertURLParamsToSearchParams({
                urlParams: params,
                productType,
                categoryId: searchParams.categoryId,
            }),
        );

        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const onChangeRangeFilter = (type: string, value: string): void => {
        const params = new URLSearchParams(location.search);
        if (params.get('page')) {
            params.delete('page');
        }
        const currentValue = params.get(type);
        if (currentValue === value) {
            params.delete(type);
        }
        params.set(type, value);

        history.replace(`${location.pathname}?${params.toString()}`);
        setSearchParams(
            convertURLParamsToSearchParams({
                urlParams: params,
                productType,
                categoryId: searchParams.categoryId,
            }),
        );

        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const onChangeFilter = (type: string, value: string): void => {
        const params = new URLSearchParams(location.search);

        if (params.get('page')) {
            params.delete('page');
        }

        const currentValues = params.getAll(type);
        if (currentValues.includes(value)) {
            // @ts-expect-error - Using polyfilled URLSearchParams.delete with two arguments
            params.delete(type, value);
        } else {
            params.append(type, value);
        }

        history.replace(`${location.pathname}?${params.toString()}`);
        setSearchParams(
            convertURLParamsToSearchParams({
                urlParams: params,
                productType,
                categoryId: searchParams.categoryId,
            }),
        );

        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    /**
     * Bulk change filter values. Removes all params of the given type and adds all the values sent.
     * @param filters Array of objects with {type, value}
     */
    const onBulkChangeFilter = (filters: Array<{ type: string; value: string }>): void => {
        if (!filters.length) return;

        const params = new URLSearchParams(location.search);

        // Remove all params
        filters.forEach(({ type }) => params.delete(type));

        // Add all new values
        filters
            .filter(({ value }) => value)
            .forEach(({ type, value }) => params.append(type, value));

        if (params.get('page')) {
            params.delete('page');
        }

        history.replace(`${location.pathname}?${params.toString()}`);
        setSearchParams(
            convertURLParamsToSearchParams({
                urlParams: params,
                productType,
                categoryId: searchParams.categoryId,
            }),
        );

        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const isFilterSelected = (type: string, value: string): boolean =>
        convertSearchParamsToURLParams(searchParams).getAll(type).includes(value);

    const activeFilters: Array<{ type: string; value: string; label: string }> = [];

    const result = {
        onSelected,
        onRemoveFilter,
        onClearAllFilters,
        onChangeRangeFilter,
        onChangeFilter,
        onBulkChangeFilter,
        onChangeParam,
        isFilterSelected,
        activeFilters,
    };

    if (productType !== BloomreachProductType.hardgood) {
        return result;
    }

    const urlParams = new URLSearchParams(location.search);
    // Since when adding a filter it is appended in the url, we can assume that the last added filter is the first in the URL
    const urlParamsOrdered = Array.from(urlParams.entries()).reverse();

    urlParamsOrdered.forEach(([type, value]) => {
        if (!['brand', 'color', 'size', 'material', 'price', 'rating', 'category'].includes(type)) {
            return;
        }

        if (type === 'category') {
            // categoriesMap will have the latest category data, meaning that on every search, the categories will be updated
            // so, when clicking on a category, then clicking on another, chances are that the previous category selected is no longer available
            // so, we need to check if the category is still available or else it will throw an error
            if (categoriesMap[value]) {
                activeFilters.push({
                    type: 'category',
                    value,
                    label: categoriesMap[value]?.name,
                });
            }
        } else {
            activeFilters.push({ type, value, label: value });
        }
    });

    return result;
};
